import Axios from 'axios';
import { action, observable } from 'mobx';
import { PageInfo, PlanParams, ProfitParams } from '@/models/data';
import { Notification } from 'element-react';
import { API } from '@/config/api';
import i18n from '@/i18n';
import { getBaseUrlBySymbol, getBaseUrlByUnderlyingType } from '@/utils/futures';
import { TriggerPriceType } from '@/pages/futures/main/orderForm/formSection';
import UnderLyingEnum from '@/@enums/UnderLyingEnum';
import { TpslConditionType } from '@/pages/futures/main/orderForm/tpslCondition';

export interface PlanItem {
  closePosition: boolean; // 是否触发全平
  createdTime: number; // 创建时间
  entrustId: string; // 委托id
  entrustType: string; // 委托类型
  isOrdinary: boolean; // 是否是普通计划单
  marketOrderLevel: number; // 市价最优档
  orderSide: OrderSide; // 买卖方向
  origQty: string; // 数量
  positionSide: PositionSide; // 持仓方向
  price: string; // 订单价格
  state: string; // 订单状态 NOT_TRIGGERED：新建委托（未触发）；TRIGGERING：触发中；TRIGGERED：已触发；USER_REVOCATION：已撤销；PLATFORM_REVOCATION：平台撤销（拒绝）；EXPIRED：已过期	string
  stopPrice: string; // 触发价格
  symbol: string; // 交易对
  timeInForce: string; // 有效方式
  triggerPriceType: string; // 触发价格类型

  // 其他配置项
  contractSize: string;
}

export interface ProfitItem {
  positionId: string;
  createdTime: number;
  entryPrice: string;
  origQty: string;
  positionSide: PositionSide;
  profitId: string;
  state: string;
  symbol: string;
  triggerProfitPrice: string;
  triggerProfitConditionType?: TpslConditionType;
  triggerProfitConditionValue?: string;
  triggerStopPrice: string;
  triggerStopConditionType?: TpslConditionType;
  triggerStopConditionValue?: string;
  triggerPriceType: TriggerPriceType;
  profitType?: 'ALL' | 'NORMAL';
  failReasonCode?: string;
  failReason?: string;

  // 其他配置信息
  contractSize: string;
  liquidationPrice: string;
  pricePrecision: number;
}

export default class Order {
  @observable planList: PlanItem[];

  @observable planListPageInfo: PageInfo;

  @observable withdrawPlanList: string[];

  @observable profitList: ProfitItem[];

  @observable profitListPageInfo: PageInfo;

  @observable withdrawProfitList: string[];

  @observable isFetchingPlanList: boolean;

  constructor() {
    this.planList = [];
    this.planListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };
    this.withdrawPlanList = [];

    this.profitList = [];
    this.profitListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };

    this.withdrawProfitList = [];

    this.isFetchingPlanList = false;
  }

  @action.bound
  setLoadingState(key: string, data: boolean) {
    // @ts-ignore
    this[key] = data;
  }

  //
  // 计划委托
  //

  @action
  setPlanList(data: PlanItem[]) {
    this.planList = data;
  }

  @action.bound
  setPlanListPageInfo(data: PageInfo) {
    const origin = { ...this.planListPageInfo };
    this.planListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchPlanList(params?: {
    symbol?: string;
    page?: number;
    size?: number;
    state?: string;
    underlyingType?: UnderLyingEnum;
  }) {
    this.setLoadingState('isFetchingPlanList', true);
    const planListPageInfo = this.planListPageInfo;

    const config: any = {
      params: {
        page: planListPageInfo.page,
        size: planListPageInfo.size,
        ...params,
      },
    };

    if (params?.symbol) {
      config.baseURL = getBaseUrlBySymbol(params.symbol);
    } else if (params?.underlyingType) {
      config.baseURL = getBaseUrlByUnderlyingType(params?.underlyingType);
    }

    Reflect.deleteProperty(config.params, 'underlyingType');

    const result: any = await Axios.get(API.ENTRUST_PLAN_LIST, config);

    if (result) {
      const { items, page, ps: size, total } = result;

      if (Array.isArray(items)) {
        this.setPlanList(items);

        this.setPlanListPageInfo({ page, size, total });
      }
    }

    this.setLoadingState('isFetchingPlanList', false);
  }

  @action.bound
  async fetchCancelPlan(id: string) {
    this.setWithdrawPlanList([...this.withdrawPlanList, id]);
    const result: any = await Axios.post(API.ENTRUST_PLAN_CANCEL, { entrustId: id }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.withdraw'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      const newList = [...this.planList];
      const index = newList.findIndex((item: PlanItem) => id === item.entrustId);
      newList.splice(index, 1);
      this.setPlanList(newList);

      const { page, size, total } = this.planListPageInfo;
      this.setPlanListPageInfo({ page, size, total: total - 1 });

      Notification.success({
        message: i18n.t('success.withdraw'),
        offset: 60,
        duration: 2000,
      });
    }

    this.setWithdrawPlanList([...this.withdrawPlanList, id]);
  }

  // 更新现有计划委托列表
  // 如果是本地更新，当前页不是第一页时没有必要更新；
  // @action.bound
  // addPlanToList(newItem: PlanItem, isLocal = false) {
  //   const { page, size, total } = this.planListPageInfo;
  //   if (isLocal) {
  //     // 如果不是第一页，没有必要将本地产生的订单加入列表；
  //     if (page !== 1) return;
  //   }
  //
  //   const newList = [newItem, ...this.planList];
  //   if (newList.length > size) {
  //     newList.pop();
  //   }
  //
  //   this.setPlanList(newList);
  //   this.setPlanListPageInfo({ page, size, total: total + 1 });
  // }

  @action.bound
  setWithdrawPlanList(data: string[]) {
    this.withdrawPlanList = data;
  }

  @action.bound
  isWithdrawingPlan(id: string) {
    return this.withdrawPlanList.includes(id);
  }

  @action.bound
  async fetchCreatePlan(params: PlanParams) {
    const result: any = await Axios.post(API.ENTRUST_PLAN_CREATE, params).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.order'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      Notification.success({
        message: i18n.t('success.order'),
        offset: 60,
        duration: 2000,
      });
    }
  }

  //
  // 止盈止损
  //
  @action
  setProfitList(data: ProfitItem[]) {
    this.profitList = data;
  }

  @action.bound
  setProfitListPageInfo(data: PageInfo) {
    const origin = { ...this.profitListPageInfo };
    this.profitListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchProfitList(params?: {
    symbol?: string;
    positionId?: string;
    page?: number;
    size?: number;
    state?: string;
  }) {
    const profitListPageInfo = this.profitListPageInfo;
    const result: any = await Axios.get(API.ENTRUST_PROFIT_LIST, {
      params: {
        page: profitListPageInfo.page,
        size: profitListPageInfo.size,
        ...params,
      },
    });

    if (result) {
      const { items, page, ps: size, total } = result;

      if (Array.isArray(items)) {
        this.setProfitList(items);
        this.setProfitListPageInfo({ page, size, total });
      }
    }
  }

  @action.bound
  async fetchWithdrawProfit(id: string) {
    this.setWithdrawProfitList([...this.withdrawProfitList, id]);
    const result: any = await Axios.post(API.ENTRUST_PROFIT_CANCEL, { profitId: id }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.withdraw'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      const newList = [...this.profitList];
      const index = newList.findIndex((item: ProfitItem) => id === item.profitId);
      newList.splice(index, 1);
      this.setProfitList(newList);

      const { page, size, total } = this.profitListPageInfo;
      this.setProfitListPageInfo({ page, size, total: total - 1 });

      Notification.success({
        message: i18n.t('success.withdraw'),
        offset: 60,
        duration: 2000,
      });
    }

    this.setWithdrawProfitList([...this.withdrawProfitList, id]);
  }

  @action.bound
  setWithdrawProfitList(data: string[]) {
    this.withdrawProfitList = data;
  }

  @action.bound
  isWithdrawingProfit(id: string) {
    return this.withdrawProfitList.includes(id);
  }

  // 更新现有止盈止损列表
  // 如果是本地更新，当前页不是第一页时没有必要更新；
  // @action.bound
  // addProfitToList(newItem: ProfitItem, isLocal = false) {
  //   const { page, size } = this.profitListPageInfo;
  //   if (isLocal) {
  //     // 如果不是第一页，没有必要将本地产生的订单加入列表；
  //     if (page !== 1) return;
  //   }
  //
  //   const newList = [{ ...newItem }, ...this.profitList];
  //   if (newList.length > size) {
  //     newList.pop();
  //   }
  //
  //   this.setProfitList(newList);
  // }

  // 修改止盈止损
  @action.bound
  async fetchUpdateProfit(params: ProfitParams) {
    const { profitId } = params;

    try {
      const result: any = await Axios.post(API.ENTRUST_PROFIT_UPDATE, params).catch(({ error }) => {
        Notification.error({
          message: error?.msg || i18n.t('failure.updateStopProfit'),
          offset: 60,
          duration: 2000,
        });
      });

      if (result) {
        const newList = [...this.profitList];
        const index = newList.findIndex((item: ProfitItem) => item.profitId === profitId);

        if (index > -1) {
          const item = newList[index];
          // @ts-ignore
          newList[index] = { ...item, ...params };
          this.setProfitList(newList);
        }

        Notification.success({
          message: i18n.t('success.updateStopProfit'),
          offset: 60,
          duration: 2000,
        });
      }

      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  }
}
